<script setup>
import {computed, ref, defineProps} from "vue";
import ChevronDown from "@/components/Icons/ChevronDown.vue";
import {t} from "@/controllerLK/GlobusLocalization";
import {useLK} from "@/controllerLK/LKstore";
import {useNewsFeedStore} from "@/store/NewsFeed/newsFeed";
import {useNotificationsStore} from "@/store/notifications";

const props = defineProps({
  list: {
    type: Array,
    required: true,
    default: () => []
  },
  isShowFullNews: {
    type: Boolean,
    default: false
  },
  title: {
    type: [String, null],
    default: null
  }
});

// TODO: Флаг отключения выбора персон. Удалить после релиза 25.12
const disableObjectSelect = true;

// Активный язык. Для добавления склонения.
const LKStore = useLK();
const notificationsStore = useNotificationsStore();
const newsFeedStore = useNewsFeedStore();

const activeLanguage = computed(() => {
  try {
    return LKStore.user.language.toLowerCase();
  } catch (error) {
    return "ru";
  }
});

// Функция для получения склонения/окончания
const getSuffix = (count) => {
  const lang = activeLanguage.value;

  if (lang === "ru") {
    const lastDigit = count % 10;
    const lastTwoDigits = count % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
      return "ов"; // объектов
    }

    if (lastDigit === 1) {
      return ""; // объект
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
      return "а"; // объекта
    }

    return "ов"; // объектов
  } else {
    // Для других языков: добавляем 's' если count !== 1
    return count === 1 ? "" : "s";
  }
};

// Флаг. Открытие списка
const isExpanded = ref(false);

// Функция. Разворачивание/Скрытие списка.
const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

// Вычисляемая функция. Общее количество объектов новости.
const objectListLength = computed(() => props.list.length);

// Вычисляемая функция. Количество объектов для раскрытия.
const objectListHiddenLength = computed(() => objectListLength.value - 3);

// Список отображаемых объектов.
const objectListView = computed(() => {
  if (isExpanded.value || props.isShowFullNews) {
    return props.list;
  } else {
    return props.list.slice(0, 3);
  }
});

const objectTypesList = ["persons", "locations", "organisations"];

// Получение текста для тултипа.
const hoveredObject = ref(null);
const getTooltipText = computed(() => {
  // TODO: Скрыто перед релизом 12.25. Удалить строку ниже.
  if (disableObjectSelect) return "";
  //

  if (!hoveredObject.value) return "";
  if (props.isShowFullNews) return "";

  const type = objectTypesList[hoveredObject.value.object_type];

  if (!type) return hoveredObject.value.name;

  const action = isObjectSelected(hoveredObject.value) ? "cancel" : "search";
  return t(`news_page_object_list.tooltip.${action}_${type}`, hoveredObject.value.name);
});

// Добавление объекта в фасет.
const addObjectToFilter = async (object) => {
  // TODO: Скрыто перед релизом 12.25. Удалить строку ниже.
  if (disableObjectSelect) return "";
  //
  if (props.isShowFullNews) return;

  const isObjectToggled = await newsFeedStore.toggleObjectInFilter(object.name, objectTypesList[object.object_type]);
  await newsFeedStore.clearNewsFeed();
  await newsFeedStore.getNews();

  if (isObjectToggled) {
    await notificationsStore.createNotify(
        t(`news_page_object_list.toast.${objectTypesList[object.object_type]}`, object.name), "success", 720);
  } else {
    await notificationsStore.createNotify(t("news_page_object_list.toast.cancel"), "success", 720);
  }
};

// Выбран ли текущий объект.
const isObjectSelected = (object) => {
  return newsFeedStore.filterObject.list.some(obj => obj === object.name);
};
</script>

<template>
  <div class="object-view">
    <div
      v-if="!props.isShowFullNews"
      class="object-view__header"
    >
      <span class="object-view__title">{{ t('news_page_body_post.label.object') }}</span>
      <span
        v-if="objectListLength === 0"
        class="object-view__subtitle"
      >{{ t('news_page_body_post.label.not_defined_set') }}</span>
    </div>

    <div
      v-if="objectListLength > 0"
      class="object-view__table-wrapper"
    >
      <span
        v-if="props.title"
        class="object-view__table-title"
      >
        {{ props.title }}
      </span>
      <div class="object-view__table">
        <div class="object-view__table-body">
          <div
            v-for="object in objectListView"
            :key="object"
            v-tooltip:bottom-left:3="getTooltipText"
            class="object-view__table-row"
            @click="addObjectToFilter(object)"
            @mouseenter="hoveredObject = object"
            @mouseleave="hoveredObject = null"
          >
            <div
              :class="{'object-view__table-col--selected': isObjectSelected(object)}"
              class="object-view__table-col object-view__table-col--title"
            >
              {{ object.name }}
            </div>
            <div
              :class="{'object-view__table-col--selected': isObjectSelected(object)}"
              class="object-view__table-col"
            >
              {{ object.description }}
            </div>
          </div>
        </div>
      </div>
      <ButtonRoundedTertiary
        v-if="objectListLength > 3 && !props.isShowFullNews"
        size="extra-small"
        class="object-view__button-expand"
        @click="toggleExpanded"
      >
        <template v-if="isExpanded">
          {{ t('news_page_object_list.buttons.collapse') }}
        </template>
        <template v-else>
          +{{ objectListHiddenLength }}
          {{ t('news_page_object_list.buttons.expand_objects', getSuffix(objectListHiddenLength)) }}
        </template>
        <ChevronDown
          class="object-view__button-icon"
          :class="{'object-view__button-icon--active': isExpanded}"
        />
      </ButtonRoundedTertiary>
    </div>
  </div>
</template>

<style scoped lang="scss">
.object-view {
  display: flex;
  flex-direction: column;
  gap: 4px;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__title {
    font-size: 14px;
    font-weight: 500;
    color: var(--text-text-pur);
  }

  &__subtitle {
    color: var(--text-text-pur);
  }

  &__button {
    &-expand {
      position: absolute;
      right: 0;
      bottom: -10px;
    }

    &-icon {
      width: 14px;
      height: 14px;

      &--active {
        transform: rotateX(180deg);
      }
    }
  }

  &__table-wrapper {
    position: relative;
  }

  &__table {
    width: 100%;
    border-radius: 8px;
    padding: 10px;
    background-color: var(--background-01);
  }

  &__table-body {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__table-title {
    text-transform: uppercase;
    font-size: 12px;
    line-height: 18px;
    font-weight: 400;
    color: var(--text-text-pur);
  }

  &__table-row {
    display: grid;
    grid-template-columns: 160px 1fr;
    grid-gap: 10px;
    padding: 6px;
    border-radius: 6px;

    // TODO: Скрыто перед релизом 12.25. Раскоментировать
    //&:hover {
    //  background-color: var(--purple-purple10);
    //}
  }

  &__table-col {
    font-size: 14px;
    line-height: 18px;
    color: var(--text-text-main);

    &--selected {
      color: var(--text-text-blue);
    }

    &--title {
      font-weight: 500;
      width: 160px;
    }
  }
}
</style>